/**
 * Scoring Engine — subscribes to eventBus events and evaluates
 * them against the lessonPlayer's pending grades.
 *
 * Listens for:
 *   library:folderSelected   → NAVIGATE grades
 *   project:itemAdded        → TEMPLATE grades
 *   properties:fieldChanged  → EDIT grades
 *   monitor:takeToProgram    → PROGRAM grades
 *   shot:triggered           → SHOT grades
 *   data:linkCreated         → DATA grades
 */

import { LessonEventType, LessonFlowState } from '@graphicsim/shared-types';
import { eventBus } from './eventBus';
import { checkPendingGrade } from './lessonPlayer';
import { lessonStore } from '../stores/lessonStore';

let unsubscribers: (() => void)[] = [];

/** Initialize scoring engine — subscribes to all relevant events */
export function initScoringEngine(): void {
  stopScoringEngine();

  // Only score during SIMULATION state
  const isActive = () => lessonStore.getState().flowState === LessonFlowState.SIMULATION;

  // Library folder navigation → NAVIGATE
  unsubscribers.push(
    eventBus.on('library:folderSelected', ({ folderId }) => {
      if (!isActive()) return;
      checkPendingGrade(LessonEventType.NAVIGATE, 'select_folder', folderId);
    }),
  );

  // Template added to project → TEMPLATE
  unsubscribers.push(
    eventBus.on('project:itemAdded', ({ item }) => {
      if (!isActive()) return;
      if (item.templateId) {
        checkPendingGrade(LessonEventType.TEMPLATE, 'add_to_project', item.templateId);
      }
    }),
  );

  // Property field changed → EDIT
  unsubscribers.push(
    eventBus.on('properties:fieldChanged', ({ itemId, fieldId, value }) => {
      if (!isActive()) return;
      // Check against fieldId (target) and value (expectedValue)
      checkPendingGrade(LessonEventType.EDIT, 'edit_field', value);
    }),
  );

  // Take to program → PROGRAM
  unsubscribers.push(
    eventBus.on('monitor:takeToProgram', () => {
      if (!isActive()) return;
      checkPendingGrade(LessonEventType.PROGRAM, 'take_to_program', 'take');
    }),
  );

  // Shot triggered → SHOT
  unsubscribers.push(
    eventBus.on('shot:triggered', ({ shotId }) => {
      if (!isActive()) return;
      checkPendingGrade(LessonEventType.SHOT, 'trigger_shot', shotId);
    }),
  );

  // Data link created → DATA
  unsubscribers.push(
    eventBus.on('data:linkCreated', ({ link }) => {
      if (!isActive()) return;
      checkPendingGrade(LessonEventType.DATA, 'create_link', link.fieldId);
    }),
  );
}

/** Clean up all event subscriptions */
export function stopScoringEngine(): void {
  unsubscribers.forEach((unsub) => unsub());
  unsubscribers = [];
}
